import type { SVGProps } from 'react';

/**
 * Brand marks for third-party services we reference.
 *
 * These marks use each brand's official colors (multi-path where needed) —
 * NOT currentColor — so they read as the service's familiar app icon even
 * at sidebar sizes. Trade-off: they don't adapt to the row's hover color.
 *
 * All icons viewBox="0 0 24 24" with no hardcoded width/height, so Tailwind
 * size-* classes control rendered dimensions. JSX-safe: camelCase attrs
 * only (clipPath, fillRule, etc.), no Adobe Illustrator namespaces, no
 * inline <style> blocks, no id collisions across multiple renders.
 */

export function GithubMark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <path
        fill="#181717"
        d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"
      />
    </svg>
  );
}

export function NotionMark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <path
        fill="#000000"
        d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.139c-.093-.514.28-.887.747-.933z"
      />
    </svg>
  );
}

export function SlackMark(props: SVGProps<SVGSVGElement>) {
  // Four-color pinwheel. Hex values are Slack's official brand colors.
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <path
        fill="#E01E5A"
        d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522z"
      />
      <path
        fill="#36C5F0"
        d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521z"
      />
      <path
        fill="#2EB67D"
        d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522z"
      />
      <path
        fill="#ECB22E"
        d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523z"
      />
    </svg>
  );
}

export function ClaudeMark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <path
        fill="#D97757"
        d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z"
      />
    </svg>
  );
}

export function LinearMark(props: SVGProps<SVGSVGElement>) {
  // Linear's brand purple with its distinctive curved-stripes glyph.
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <path
        fill="#5E6AD2"
        d="M.403 13.795A12.005 12.005 0 0 0 10.204 23.6zM.002 10.328L13.672 24C15 23.75 16.27 23.3 17.43 22.68L1.323 6.573C.705 7.73.254 9 .002 10.33zM2.422 5.051L18.95 21.578A12.06 12.06 0 0 0 21.578 18.95L5.05 2.422C3.955 3.164 3.1 4.016 2.37 5.051zM6.934 1.324L22.675 17.066C23.735 15.078 24.001 12.95 24 12 24 5.373 18.627 0 12 0c-1.852 0-3.606.42-5.067 1.324z"
      />
    </svg>
  );
}

export function FigmaMark(props: SVGProps<SVGSVGElement>) {
  // Five-circle Figma mark with its official palette.
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <path fill="#0ACF83" d="M8 24c2.208 0 4-1.792 4-4v-4H8c-2.208 0-4 1.792-4 4s1.792 4 4 4z" />
      <path fill="#A259FF" d="M4 12c0-2.208 1.792-4 4-4h4v8H8c-2.208 0-4-1.792-4-4z" />
      <path fill="#F24E1E" d="M4 4c0-2.208 1.792-4 4-4h4v8H8C5.792 8 4 6.208 4 4z" />
      <path fill="#FF7262" d="M12 0h4c2.208 0 4 1.792 4 4s-1.792 4-4 4h-4V0z" />
      <path fill="#1ABCFE" d="M20 12c0 2.208-1.792 4-4 4s-4-1.792-4-4 1.792-4 4-4 4 1.792 4 4z" />
    </svg>
  );
}

export function GmailMark(props: SVGProps<SVGSVGElement>) {
  // Gmail envelope in Google brand palette.
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <path fill="#4285F4" d="M1.636 21c-.904 0-1.636-.732-1.636-1.636V7.457L3.273 9.82l2.182 1.636v9.545H1.636z" />
      <path fill="#34A853" d="M18.545 21V11.456l2.182-1.636L24 7.458v11.907c0 .904-.732 1.636-1.636 1.636z" />
      <path fill="#FBBC04" d="M24 7.458L18.545 11.456V2.639l1.528-1.145C21.69.281 24 1.435 24 3.458v4z" />
      <path fill="#EA4335" d="M5.455 11.456V2.639L12 7.548l6.545-4.909v8.817L12 16.365z" />
      <path fill="#C5221F" d="M0 7.458v-4c0-2.023 2.309-3.178 3.927-1.964l1.528 1.145v8.817L3.273 9.82z" />
    </svg>
  );
}

export function GoogleCalendarMark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg version="1.1" id="Livello_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 200 200" enable-background="new 0 0 200 200" aria-hidden {...props}>
      <g>
        <g transform="translate(3.75 3.75)">
          <path fill="#FFFFFF" d="M148.882,43.618l-47.368-5.263l-57.895,5.263L38.355,96.25l5.263,52.632l52.632,6.579l52.632-6.579
            l5.263-53.947L148.882,43.618z"/>
          <path fill="#1A73E8" d="M65.211,125.276c-3.934-2.658-6.658-6.539-8.145-11.671l9.132-3.763c0.829,3.158,2.276,5.605,4.342,7.342
            c2.053,1.737,4.553,2.592,7.474,2.592c2.987,0,5.553-0.908,7.697-2.724s3.224-4.132,3.224-6.934c0-2.868-1.132-5.211-3.395-7.026
            s-5.105-2.724-8.5-2.724h-5.276v-9.039H76.5c2.921,0,5.382-0.789,7.382-2.368c2-1.579,3-3.737,3-6.487
            c0-2.447-0.895-4.395-2.684-5.855s-4.053-2.197-6.803-2.197c-2.684,0-4.816,0.711-6.395,2.145s-2.724,3.197-3.447,5.276
            l-9.039-3.763c1.197-3.395,3.395-6.395,6.618-8.987c3.224-2.592,7.342-3.895,12.342-3.895c3.697,0,7.026,0.711,9.974,2.145
            c2.947,1.434,5.263,3.421,6.934,5.947c1.671,2.539,2.5,5.382,2.5,8.539c0,3.224-0.776,5.947-2.329,8.184
            c-1.553,2.237-3.461,3.947-5.724,5.145v0.539c2.987,1.25,5.421,3.158,7.342,5.724c1.908,2.566,2.868,5.632,2.868,9.211
            s-0.908,6.776-2.724,9.579c-1.816,2.803-4.329,5.013-7.513,6.618c-3.197,1.605-6.789,2.421-10.776,2.421
            C73.408,129.263,69.145,127.934,65.211,125.276z"/>
          <path fill="#1A73E8" d="M121.25,79.961l-9.974,7.25l-5.013-7.605l17.987-12.974h6.895v61.197h-9.895L121.25,79.961z"/>
          <path fill="#EA4335" d="M148.882,196.25l47.368-47.368l-23.684-10.526l-23.684,10.526l-10.526,23.684L148.882,196.25z"/>
          <path fill="#34A853" d="M33.092,172.566l10.526,23.684h105.263v-47.368H43.618L33.092,172.566z"/>
          <path fill="#4285F4" d="M12.039-3.75C3.316-3.75-3.75,3.316-3.75,12.039v136.842l23.684,10.526l23.684-10.526V43.618h105.263
            l10.526-23.684L148.882-3.75H12.039z"/>
          <path fill="#188038" d="M-3.75,148.882v31.579c0,8.724,7.066,15.789,15.789,15.789h31.579v-47.368H-3.75z"/>
          <path fill="#FBBC04" d="M148.882,43.618v105.263h47.368V43.618l-23.684-10.526L148.882,43.618z"/>
          <path fill="#1967D2" d="M196.25,43.618V12.039c0-8.724-7.066-15.789-15.789-15.789h-31.579v47.368H196.25z"/>
        </g>
      </g>
    </svg>
  );
}

export function OnePasswordMark(props: SVGProps<SVGSVGElement>) {
  // 1Password blue circle with white "1" key-ring glyph.
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <circle cx="12" cy="12" r="12" fill="#0572EC" />
      <path
        fill="#FFFFFF"
        d="M12 5.4a6.6 6.6 0 1 0 0 13.2 6.6 6.6 0 0 0 0-13.2zM10.4 8.9c0-.5.4-.9.9-.9h1.4c.5 0 .9.4.9.9v6.2c0 .5-.4.9-.9.9h-1.4a.9.9 0 0 1-.9-.9v-3.1l-.7.5a.45.45 0 0 1-.6-.7l1.3-1z"
      />
    </svg>
  );
}

export function VercelMark(props: SVGProps<SVGSVGElement>) {
  // Vercel's signature black triangle.
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <path fill="#000000" d="M24 22.525H0l12-21.05 12 21.05z" />
    </svg>
  );
}

export function PostmanMark(props: SVGProps<SVGSVGElement>) {
  // Postman orange circle + white satellite glyph.
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <circle cx="12" cy="12" r="12" fill="#FF6C37" />
      <path
        fill="#FFFFFF"
        d="M16.07 8.23a3.1 3.1 0 0 0-4.38 0l-4.08 4.08 4.37 4.38 4.09-4.08a3.1 3.1 0 0 0 0-4.38zm-.86 3.52l-3.22 3.22-.83-.83 3.22-3.22a.21.21 0 0 1 .3 0 .21.21 0 0 1 .03.3z"
      />
    </svg>
  );
}

export function PostHogMark(props: SVGProps<SVGSVGElement>) {
  // PostHog brand yellow + black stacked-bars glyph (abstract analytics).
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <rect width="24" height="24" rx="3" fill="#F9BD2B" />
      <path
        fill="#000000"
        d="M6 16h2.2v-3.4H6zm3.2 0h2.2v-6H9.2zm3.2 0h2.2V8h-2.2zm3.2 0H18V5h-2.4z"
      />
    </svg>
  );
}

export function SentryMark(props: SVGProps<SVGSVGElement>) {
  // Sentry dark-purple background with white arch glyph (simplified).
  return (
    <svg viewBox="0 0 24 24" aria-hidden {...props}>
      <rect width="24" height="24" rx="3" fill="#362D59" />
      <path
        fill="#FFFFFF"
        d="M12 6c-.38 0-.72.22-.88.55l-1.54 3.2a5.3 5.3 0 0 1 3 3.15H11a4 4 0 0 0-2.14-2.13l-.8 1.66A2.2 2.2 0 0 1 10 14h-4a.3.3 0 0 1-.26-.45l1.72-3.57a.3.3 0 0 1 .55.26l-.68 1.4a5.7 5.7 0 0 1 2.82 3.14H11l-.52 1.07a7.4 7.4 0 0 0-3.38-3.96l-.64 1.32H15.3a.3.3 0 0 0 .27-.44l-2.7-5.62A.97.97 0 0 0 12 6z"
      />
    </svg>
  );
}
